import React from "react";
import { TextField } from "Components/Base/Inputs/TextField";
import { InputAdornment } from "Components/Base/Inputs/InputAdornment";
import { IconButton } from "Components/Base/Inputs/IconButton";
import { Clear as ClearIcon, Search as SearchIcon } from "Components/Icons";
import styled from "styled-components";

/**
 * SearchField Component
 *
 * A custom search field component with integrated clear and search functionality.
 * The component supports two distinct search modes:
 *
 * **onType Mode (default):**
 * - Search is triggered automatically while typing (via onChange)
 * - When input has value: shows clear button (X) and search icon that acts as secondary clear button
 * - When input is empty: shows only search icon (disabled)
 *
 * **onSubmit Mode:**
 * - Search is triggered explicitly via search button click, Enter key, or blur event
 * - When input has value: shows clear button (X) and functional search button
 * - When input is empty: shows only search icon (disabled)
 *
 * @component
 * @example
 * import { useState } from "react";
 * import SearchField from "Components/Fragment/SearchField";
 *
 * function MyComponent() {
 *   const [searchValue, setSearchValue] = useState("");
 *
 *   const handleSearch = () => {
 *     console.log("Searching for:", searchValue);
 *   };
 *
 *   const handleClear = () => {
 *     setSearchValue("");
 *   };
 *
 *   return (
 *     <SearchField
 *       placeholder="Search products..."
 *       value={searchValue}
 *       onChange={(e) => setSearchValue(e.target.value)}
 *       onSearch={handleSearch}
 *       onClear={handleClear}
 *       clearLabel="Clear search"
 *       searchLabel="Perform search"
 *       searchMode="onSubmit"
 *       autoFocus
 *     />
 *   );
 * }
 *
 * @param {string} props.placeholder - Placeholder text displayed when input is empty
 * @param {string} [props.ariaLabel] - Accessibility label for the input field (defaults to placeholder if not provided)
 * @param {string} props.clearLabel - Accessibility label for the clear button (required for screen readers)
 * @param {string} props.searchLabel - Accessibility label for the search button (required for screen readers)
 * @param {string} props.value - Controlled value of the search input
 * @param {(event: React.ChangeEvent<HTMLInputElement>) => void} props.onChange - Callback fired when input value changes (in onType mode)
 * @param {() => void} props.onClear - Callback fired when clear button is clicked (required)
 * @param {() => void} props.onSearch - Callback fired when search is triggered (in onSubmit mode)
 * @param {(event: React.FocusEvent<HTMLInputElement>) => void} [props.onBlur] - Callback fired when input loses focus (only attached in 'onSubmit' mode)
 * @param {string} [props.width] - CSS width value for the search field (e.g., "300px", "100%", "20rem")
 * @param {boolean} [props.fullWidth=false] - Whether the search field should expand to fill its container width
 * @param {'onType' | 'onSubmit'} [props.searchMode='onType'] - Determines when search is triggered
 * @param {boolean} [props.autoFocus=false] - Whether the input should automatically receive focus when component mounts
 * @param {Object} [props.sx] - MUI system props for additional styling customization
 *
 * @returns {JSX.Element} A styled search input with integrated clear and search functionality
 */

const StyledSearchIconTextField = styled(TextField)`
  .MuiInputBase-root {
    padding-right: 4px !important;
    ${({ width }) => width && `width: ${width};`}
  }
`;

const SearchField = ({
  placeholder,
  ariaLabel,
  clearLabel,
  searchLabel,
  value,
  onChange,
  onClear,
  onSearch,
  onBlur,
  width,
  fullWidth = false,
  searchMode = "onType",
  autoFocus = false,
  sx,
}) => {
  const displayClearButton = !!value;
  const displaySearchButton = searchMode === "onSubmit" || !value;

  const handleKeyPress = (event) => {
    if (event.key === "Enter" && value && searchMode === "onSubmit") {
      onSearch();
    }
  };

  return (
    <StyledSearchIconTextField
      placeholder={placeholder}
      ariaLabel={ariaLabel ?? placeholder}
      value={value}
      onChange={onChange}
      onBlur={searchMode === "onSubmit" ? onBlur : undefined}
      onKeyPress={handleKeyPress}
      InputProps={{
        endAdornment: (
          <InputAdornment position="end">
            {displayClearButton && (
              <IconButton
                title={value ? clearLabel : placeholder}
                onClick={onClear}
                icon={<ClearIcon />}
                disabled={!value}
              />
            )}
            {displaySearchButton && (
              <IconButton
                title={value ? searchLabel : placeholder}
                onClick={searchMode === "onType" ? onClear : onSearch}
                icon={<SearchIcon />}
                disabled={!value}
              />
            )}
          </InputAdornment>
        ),
      }}
      width={width}
      fullWidth={fullWidth}
      autoFocus={autoFocus}
      sx={sx}
    />
  );
};

export default SearchField;
